import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Skeleton, Stack } from "@mui/material";
import { DataGrid, GridColumnVisibilityModel, GridRowId, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppMenuKey, AppMenuSectionItem, AppRoutes } from "../../../../common/routesUtils";
import NumericValueWidget from "../../../../components/charts/NumericValueWidget";
import DropdownSelect from "../../../../components/common/DropdownSelect";
import { CustomColumnMenu } from "../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../components/common/table/TableData/TableDataFooter";
import TableSelectActions from "../../../../components/common/table/TableData/TableSelectActions";
import { useCustomTableDataStyles } from "../../../../components/common/table/TableData/useCustomTableDataStyles";
import { View } from "../../../../components/layout/View";
import PaperCard from "../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../../config/page.types";
import { useBreadcrumbs } from "../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../context/PageConfigrations/usePageConfigurationState";
import { ResultsItemsListPayload, ThirdPartyTripsPageDTO } from "../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../hooks/useResource";
import { useCurrentTheme } from "../../../../theme/theme";
import CustomNoRowsOverlay from "../TransportGreenrouter/components/CustomNoRowsOverlay";
import { colsData, getColumns, getRows } from "./data";

interface Props {
	menuKeys?: Array<AppMenuKey>;
	path?: Array<AppMenuSectionItem>;
}

const SORT_OVERRIDE: Partial<Record<string, string>> = {
	transportType: "transport-type",
	isCertified: "is-certified",
	startDate: "start-date",
	endDate: "end-date",
};

type EmissionsKey = "co2Ttw" | "co2Wtw";

const TransportThirdParty: FC<Props> = ({ menuKeys, path }) => {
	const { t } = useTranslation("result.transport");
	const navigate = useNavigate();
	const tableSx = useCustomTableDataStyles();
	const { theme } = useCurrentTheme();
	const { visitPage } = useBreadcrumbs();

	const containerRef = useRef<HTMLDivElement | null>(null);

	const { appliedFilters } = useFiltersRegistry();

	// Page configuration state
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransportThirdParty, {
		emissionsKey: "co2Wtw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
	});

	// Pagination model for DataGrid
	const paginationModel = {
		page: config.page,
		pageSize: config.pageSize,
	};

	// Sorting model for DataGrid
	const sortModel: GridSortModel = config.sortField ? [{ field: config.sortField, sort: config.sortDirection }] : [];

	const handleSortModelChange = (model: GridSortModel) => {
		const firstSort = model[0];

		setConfig({
			sortField: firstSort?.field,
			sortDirection: firstSort?.sort || undefined,
		});
	};

	// Change selected emission type
	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as EmissionsKey });
	};

	// Dropdown options for emission selection
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	// Track selected rows and their IDs
	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

	useEffect(() => {
		setSelectedRowIds({ type: "include", ids: new Set() });

		if (config.page !== 0) {
			setConfig({ page: 0 });
		}
	}, [appliedFiltersKey]);

	const sortBy = sortModel[0]?.field ? (SORT_OVERRIDE[sortModel[0].field] ?? sortModel[0].field) : undefined;

	const sortDir = sortModel[0]?.sort;

	const { data, isLoading, reload } = useReadResource<ResultsItemsListPayload, ThirdPartyTripsPageDTO>(
		{
			route: ApiRoutes.ResultsTripsThirdParty,
			query: {
				page: config.page,
				pageSize: config.pageSize,
				...(sortBy && { sortBy }),
				...(sortDir && { sortDir }),
			},
		},
		{
			readMethod: "post",
		},
		{ ...appliedFilters.payload }
	);

	const handlePaginationChange = (model: { page: number; pageSize: number }) => {
		setConfig({
			page: model.page,
			pageSize: model.pageSize,
		});
	};

	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds(newSelection);
	};

	const handleRowClick = (params: any) => {
		const id = params.row.id;
		visitPage(PageType.ResultsTransportThirdPartyDetail, false, id);
		navigate(AppRoutes.ResultsTransportThirdPartyDetail.replace(":id", String(id)));
	};

	const textPaletteColor = theme.palette.primary.contrastText;

	// Default visibility model for all columns
	const defaultColumnVisibility: GridColumnVisibilityModel = useMemo(() => {
		const visibleColumns = colsData(t)
			.filter((col) => {
				if (col.field === "tCo2eTtw") return config.emissionsKey === "co2Ttw";
				if (col.field === "tCo2eWtw") return config.emissionsKey === "co2Wtw";
				return true;
			})
			.map((col) => col.field);

		return visibleColumns.reduce((acc, field) => {
			acc[field] = true;
			return acc;
		}, {} as GridColumnVisibilityModel);
	}, [config.emissionsKey, t]);

	// Merge parent model with defaults on model change
	const mergedVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
		return { ...defaultColumnVisibility, ...config.columnVisibilityModel };
	}, [defaultColumnVisibility, config.columnVisibilityModel]);

	const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
		const visibleColumns = Object.values(newModel).filter(Boolean).length;
		if (visibleColumns === 0) return;

		setConfig({ columnVisibilityModel: newModel }); // Persist to parent / localStorage
	};

	const rows = useMemo(() => data && getRows(data, t), [data, t]);
	const columns = useMemo(
		() => getColumns(colsData(t), t, textPaletteColor, config.emissionsKey),
		[t, textPaletteColor, config]
	);

	return (
		<View pageType={PageType.ResultsTransportThirdParty} sx={{ maxWidth: "100vw", overflowX: "hidden" }}>
			<Grid container spacing={2}>
				<Grid size={12} display={"flex"} justifyContent={"flex-end"}>
					<DropdownSelect
						text={t("display for")}
						value={config.emissionsKey}
						loading={isLoading}
						onChange={handleEmissionChange}
						dropdownItems={dropdownSelectEmissions}
						variant="standard"
					/>
				</Grid>
				<Grid size={6} height={"150px"}>
					<NumericValueWidget
						title={t("emissions")}
						value={data?.totalEmissions?.value}
						measure={data?.totalEmissions?.measure}
						isLoading={isLoading}
						onReload={reload}
					/>
				</Grid>
				<Grid size={6}>
					<NumericValueWidget
						title={t("number of transports")}
						value={data?.itemsCount}
						isLoading={isLoading}
						onReload={reload}
					/>
				</Grid>
				<Grid size={12} minHeight={500}>
					{isLoading ? (
						<Skeleton variant="rounded" height="100%" width="100%" />
					) : (
						<PaperCard
							ref={containerRef}
							sxContent={{ padding: 0 }}
							header={
								<PaperCardHeader
									title={t("list third party transport")}
									isLoading={isLoading}
									onReload={reload}
									containerRef={containerRef}
								/>
							}
							content={
								<>
									{!isLoading && data && (
										<TableSelectActions
											selectedRowsId={
												selectedRowIds.type === "include"
													? Array.from(selectedRowIds.ids)
													: rows
															?.filter(
																(row: { id: GridRowId }) =>
																	!selectedRowIds.ids.has(row.id)
															)
															.map((row: { id: GridRowId }) => row.id)
											}
										/>
									)}
									{/* Data grid with consumptions */}
									<Stack sx={{ width: "100%", height: 450 }}>
										<DataGrid
											getRowId={(row) => row.id}
											rows={data ? getRows(data, t) : []}
											columns={columns}
											rowHeight={56}
											checkboxSelection
											rowSelectionModel={selectedRowIds}
											onRowSelectionModelChange={handleRowSelection}
											onRowClick={handleRowClick}
											hideFooterSelectedRowCount
											pagination
											paginationMode="server"
											keepNonExistentRowsSelected
											rowCount={data?.itemsCount || 0}
											pageSizeOptions={[10, 25, 50]}
											paginationModel={paginationModel}
											onPaginationModelChange={handlePaginationChange}
											sortingMode="server"
											sortModel={sortModel}
											onSortModelChange={handleSortModelChange}
											disableColumnFilter
											disableDensitySelector
											style={{ cursor: "pointer" }}
											slots={{
												noRowsOverlay: () => <CustomNoRowsOverlay />,
												footer: CustomDataGridFooter,
												columnMenu: CustomColumnMenu,
											}}
											// Use local state for column visibility
											columnVisibilityModel={mergedVisibilityModel}
											onColumnVisibilityModelChange={handleColumnVisibilityChange}
											sx={tableSx}
										/>
									</Stack>
								</>
							}
						/>
					)}
				</Grid>
			</Grid>
		</View>
	);
};

export default TransportThirdParty;
